import apiService from "../services/api.service";

export interface TaskTemplate {
	task_template_id: number;
	task_name: string;
	task_type: "manual" | "feedback";
	task_order: number;
	requires_passenger: boolean;
	location: string;
	instructions: string;
}

export interface ExtraEventTask {
	task_id: number;
	extra_id: number;
	event_id: number;
	task_template_id: number;
	passenger_id: number | null;
	passenger_number: number | null;
	status: "pending" | "completed";
	completed_at: string | null;
	created_at: string;
	display_order: number;
	// Joined fields from task_template
	task_name?: string;
	task_type?: string;
	task_order?: number;
	requires_passenger?: boolean;
	location?: string;
	instructions?: string;
	// Enhanced instructions with special tasks
	enhanced_instructions?: string;
	// Subtitle for multi-checkin tasks
	subtitle?: string;
	// Passenger info for context
	passenger_first_name?: string;
	passenger_last_name?: string;
	passenger_flight_number?: string;
	passenger_airline?: string;
	bags_per_pax?: number;
	// Sequential completion validation
	canComplete?: boolean;
	// Supervisor enablement control (for tasks 6, 7, 9)
	can_be_completed?: boolean;
}

export interface TasksResponse {
	tasks: ExtraEventTask[];
	count: number;
}

export interface SpecialTask {
	special_task_id: number;
	special_task_name: string;
	description: string;
	associated_task_template_id: number;
	is_active: boolean;
}

export interface TaskStats {
	totalTasks: number;
	completedTasks: number;
	pendingTasks: number;
	completionPercentage: number;
}

export interface TaskStatsResponse {
	stats: TaskStats;
}

export interface TaskCompletionResponse {
	task_id: number;
	extra_id: number;
	event_id: number;
	task_template_id: number;
	passenger_id: number | null;
	passenger_number: number | null;
	status: "completed";
	completed_at: string;
	created_at: string;
}

/**
 * Get all tasks for a specific extra and event
 */
export const getTasksForExtra = async (
	extraId: number,
	eventId: number
): Promise<TasksResponse> => {
	const response = await apiService.get(`/tasks/${extraId}/${eventId}`);
	return response.data;
};

/**
 * Complete a specific task
 */
export const completeTask = async (
	taskId: number
): Promise<TaskCompletionResponse> => {
	const response = await apiService.put(`/tasks/complete/${taskId}`);
	return response.data;
};

/**
 * Get task statistics for an event
 */
export const getTaskStats = async (
	eventId: number
): Promise<TaskStatsResponse> => {
	const response = await apiService.get(`/tasks/stats/${eventId}`);
	return response.data;
};

/**
 * Get all task templates (admin only)
 */
export const getTaskTemplates = async (): Promise<{
	templates: TaskTemplate[];
}> => {
	const response = await apiService.get("/tasks/templates");
	return response.data;
};

/**
 * Create tasks for an extra/event (manual trigger for testing)
 */
export const createTasksForExtra = async (
	extraId: number,
	eventId: number
): Promise<{ extraId: number; eventId: number }> => {
	const response = await apiService.post(`/tasks/create/${extraId}/${eventId}`);
	return response.data;
};

/**
 * Auto-complete check-in task (called by staff during check-in process)
 * Note: These endpoints use staff authentication and are called from supervisor systems
 */
export const completeCheckInTask = async (
	extraId: number,
	eventId: number
): Promise<{ extraId: number; eventId: number }> => {
	const response = await apiService.post(
		`/tasks/checkin/${extraId}/${eventId}`
	);
	return response.data;
};

/**
 * Auto-complete check-out task (called by staff during check-out process)
 * Note: These endpoints use staff authentication and are called from supervisor systems
 */
export const completeCheckOutTask = async (
	extraId: number,
	eventId: number
): Promise<{ extraId: number; eventId: number }> => {
	const response = await apiService.post(
		`/tasks/checkout/${extraId}/${eventId}`
	);
	return response.data;
};

/**
 * Revert a completed task back to pending
 */
export const revertTask = async (
	taskId: number
): Promise<TaskCompletionResponse> => {
	const response = await apiService.put(`/tasks/revert/${taskId}`);
	return response.data;
};

/**
 * Get special tasks assigned to a passenger
 */
export const getPassengerSpecialTasks = async (
	passengerId: number
): Promise<{ specialTasks: SpecialTask[] }> => {
	const response = await apiService.get(`/tasks/passenger-special/${passengerId}`);
	return response.data;
};
